import type { RoiId, StructureSetId } from '../../changes/ids';
import {
    hasFileBorneChange,
    type PendingStructureSetChanges,
} from '../../changes/PendingChangesStore';
import { DICOMApprovalStatus } from '../../dicom/dicomConstants';
import { domainTrace } from '../../diagnostics/domainTrace';
import type { Patient } from '../../patient/patient';
import type { StructureSet } from '../../patient/structureset';

import type {
    ConnectorVersionToken,
    PatientPersistenceRef,
} from '../../persistence/PersistenceRef';
import type {
    SealedSaveIntent,
    SealedRoiReviews,
    SealedRoiRowEdit,
    SealedStructureSet,
} from '../save/SealedSaveIntent';
import type { PersistenceRefReader } from '../../persistence/PersistenceRegistry';
import type { DatasetSaver, SaveScope } from '../save/DatasetSaver';
import { deleteDatasetRow, inScope, mapSaveErrors } from '../save/DatasetSaver';
import type { SaveEffect, SaveEntryResult } from '../save/SaveEffect';
import type { SaveOutcome } from '../save/SaveOutcome';
import type {
    PersistedApprovalStatus,
    PersistedStructureSetDetail,
    StructureSetPersistenceGateway,
} from '../../persistence/StructureSetPersistenceGateway';
import { DicomProcessor } from '../dicomProcessor';
import { generateUID } from '~/utils/dicomUid';

/** Maps the domain approval enum onto the approval endpoint's wire values. */
function toWireApproval(status: DICOMApprovalStatus): PersistedApprovalStatus {
    switch (status) {
        case DICOMApprovalStatus.APPROVED:
            return 'Approved';
        case DICOMApprovalStatus.REJECTED:
            return 'Rejected';
        default:
            return 'Unapproved';
    }
}

/**
 * Persists pending structure-set changes to the connector database.
 * Driven by the {@link PatientSaveService} orchestrator, which owns the
 * save attempt (intent capture, engine flush, completeSave, report) —
 * this saver is called FIRST after the engine flush so its synchronous
 * blob serialization happens before any await, and the uploaded bytes
 * match the captured intent exactly.
 *
 * Sequence per attempt (deterministic; sets processed in stable id
 * order, each independently — one failure never blocks the others):
 * 2. Deleted sets issue a DELETE; new sets create a server skeleton, adopt
 *    its Study/Series UID into the local dataset (the backend keeps the
 *    row's Series UID on content saves — a mismatched file would disagree
 *    with its row forever) and then PUT their content; modified sets PUT
 *    content with a fresh SOP Instance UID (an edited RTSTRUCT is a new
 *    SOP instance) under the load-time If-Match token. Approval
 *    transitions go through the approval endpoint — an approval last, and
 *    any other transition first (a server copy still approved would 409
 *    the content PUT, and every transition clears the structures' own
 *    reviews). Those reviews travel in the content PUT itself, so a review
 *    and the geometry it judges are stored together or not at all; a save
 *    without content states them through the structure-reviews endpoint.
 *    Colour-only edits on an approved set use the per-structure PATCH.
 * 3. A stale 409 or approved-lock surfaces as a per-set conflict; the
 *    pending entry is retained (the indicator must never show clean for
 *    unsaved work). Successful sets record their new version token.
 *
 * Overwrite (after the user's explicit acknowledgement): re-fetches the
 * row's fresh token and retries with it — unless the server copy became
 * APPROVED, which is never overwritten (reload is the only path).
 */
export class StructureSetSaveService implements DatasetSaver {
    readonly entity = 'structureSet' as const;

    constructor(
        private readonly refs: PersistenceRefReader,
        private readonly gateway: StructureSetPersistenceGateway
    ) {}

    /**
     * FULLY SYNCHRONOUS seal: serializes every already-persisted content
     * change into the sealed intent. The orchestrator calls this right
     * after the engine flush with NO await in between — the user can keep
     * editing during the (possibly long) awaits that follow, and the
     * uploaded bytes must correspond exactly to the captured intent, not
     * to whatever the contours look like later. Entries in
     * `lostFlushIds` seal nothing: their domain copy is stale, they are
     * failed in the network pass (SR-WOR-H-23-4), and serializing would
     * churn the SOP UID of bytes that must never be sent.
     */
    seal(
        patient: Patient,
        entries: readonly PendingStructureSetChanges[],
        lostFlushIds: ReadonlySet<StructureSetId>
    ): ReadonlyMap<StructureSetId, SealedStructureSet> {
        const sealed = new Map<StructureSetId, SealedStructureSet>();
        for (const entry of entries) {
            if (entry.status === 'deleted') {
                continue; // Nothing to transmit but the registry's ref.
            }
            const ss = patient.getStructureSetById(entry.structureSetId);
            if (!ss) {
                continue; // Reported as failed in the per-entry pass.
            }

            const base = {
                approvalStatus: ss.approvalStatus,
                reviewerName: ss.reviewerName,
                roiRowEdits: sealRoiRowEdits(entry, ss),
                rois: ss.rois.map((roi) => ({
                    roiId: roi.id,
                    roiNumber: roi.dicomRoi.roiNumberInFile,
                })),
                lockedRoiIds: ss.rois
                    .filter((roi) => roi.locked)
                    .map((roi) => roi.id),
                modelStructures: {
                    modelGenerated: ss.rois
                        .filter((roi) => roi.modelGenerated)
                        .map((roi) => roi.id),
                    contourEdited: ss.rois
                        .filter((roi) => roi.contourEditedSinceGeneration)
                        .map((roi) => roi.id),
                },
                roiReviews: sealRoiReviews(entry, ss),
            };

            if (entry.status === 'new') {
                const imageSeriesUid = ss.image?.seriesUid;
                if (!imageSeriesUid) {
                    sealed.set(entry.structureSetId, {
                        ...base,
                        bytes: null,
                        sealFailure:
                            'Structure set has no referenced image series',
                    });
                    continue;
                }
                // The identity is client-minted at creation and already in
                // the dataset — creation copies the image's Study UID and
                // mints Series/SOP UIDs (deliberately sharing a series with
                // an existing same-FoR MVision set where one exists). The
                // sealed bytes are therefore FINAL: the skeleton create asks
                // the server to adopt this identity rather than the reverse.
                const dataset = ss.dicomStructureSet.datasetWrapper.dataset as {
                    StudyInstanceUID?: string;
                    SeriesInstanceUID?: string;
                    SOPInstanceUID?: string;
                };
                if (
                    !dataset.StudyInstanceUID ||
                    !dataset.SeriesInstanceUID ||
                    !dataset.SOPInstanceUID
                ) {
                    sealed.set(entry.structureSetId, {
                        ...base,
                        bytes: null,
                        sealFailure:
                            'Structure set is missing its DICOM identity ' +
                            'and cannot be saved.',
                    });
                    continue;
                }
                sealed.set(entry.structureSetId, {
                    ...base,
                    bytes: ss.export(),
                    newIdentity: {
                        studyInstanceUid: dataset.StudyInstanceUID,
                        seriesInstanceUid: dataset.SeriesInstanceUID,
                        sopInstanceUid: dataset.SOPInstanceUID,
                        referencedImageSeriesUid: imageSeriesUid,
                        displayName: ss.structureSetLabel,
                        provenanceTaskId: ss.generationModel?.provenanceTaskId,
                    },
                });
                continue;
            }

            if (!entry.contentDirty || lostFlushIds.has(entry.structureSetId)) {
                sealed.set(entry.structureSetId, { ...base, bytes: null });
                continue;
            }
            // An edited RTSTRUCT is a new SOP instance (design: saves
            // replace the working copy; the backend takes the SOP UID from
            // the file).
            DicomProcessor.setSopInstanceUid(
                ss.dicomStructureSet.datasetWrapper,
                generateUID()
            );
            sealed.set(entry.structureSetId, { ...base, bytes: ss.export() });
        }

        return sealed;
    }

    /** The per-entry network pass over a sealed intent. */
    async saveEntries(
        sealed: SealedSaveIntent,
        patientRef: PatientPersistenceRef,
        overwrite: ReadonlySet<string> | null,
        scope: SaveScope = 'all'
    ): Promise<SaveEntryResult[]> {
        const { lostFlushIds } = sealed;
        const ordered = [...sealed.intent.state.byStructureSet.values()]
            .filter((entry) => inScope(entry.status, scope))
            .sort((a, b) => a.structureSetId.localeCompare(b.structureSetId));
        const results: SaveEntryResult[] = [];
        for (const entry of ordered) {
            // A set that lost edits to a failed switch-away flush must
            // never have its stale domain copy presented as saved
            // (SR-WOR-H-23-4). It stays pending; re-activating the set in
            // the viewer shows its true state and clears the mark.
            // (Deletions are unaffected — there is nothing to upload.)
            if (
                entry.status !== 'deleted' &&
                lostFlushIds.has(entry.structureSetId)
            ) {
                results.push({
                    outcome: {
                        kind: 'failed',
                        entity: 'structureSet',
                        id: entry.structureSetId,
                        message:
                            'Contour edits failed to leave the editor when ' +
                            'the structure set was closed. Open the ' +
                            'structure set to verify its contours, then ' +
                            'save again.',
                    },
                    effects: [],
                });
                continue;
            }
            results.push(
                await this.saveOne(
                    entry,
                    patientRef,
                    sealed.structureSets.get(entry.structureSetId) ?? null,
                    overwrite?.has(entry.structureSetId) ?? false
                )
            );
        }

        return results;
    }

    private async saveOne(
        entry: PendingStructureSetChanges,
        patientRef: PatientPersistenceRef,
        sealed: SealedStructureSet | null,
        overwrite: boolean
    ): Promise<SaveEntryResult> {
        const id = entry.structureSetId;
        domainTrace.service('StructureSetSaveService.saveOne', {
            id,
            status: entry.status,
        });
        // Owned OUTSIDE the error mapping: a skeleton that committed
        // before a later step threw has already earned its setRef, and it
        // must reach the orchestrator with the failed outcome or the
        // retry duplicates the row.
        const effects: SaveEffect[] = [];
        const outcome = await mapSaveErrors(this.entity, id, async () => {
            if (entry.status === 'deleted') {
                // The server refuses to delete an approved set with the
                // approved-lock 409 → reload-only conflict, mapped above.
                return deleteDatasetRow(
                    this.refs,
                    this.entity,
                    id,
                    (ref) => this.gateway.deleteStructureSet(ref),
                    effects
                );
            }
            // The seal ran in the no-await window; a missing entry means the
            // set was already gone when the save began. No live fallback.
            if (!sealed) {
                return {
                    kind: 'failed',
                    entity: 'structureSet',
                    id,
                    message: 'Structure set no longer exists in the session',
                };
            }
            if (sealed.sealFailure) {
                return {
                    kind: 'failed',
                    entity: 'structureSet',
                    id,
                    message: sealed.sealFailure,
                };
            }
            if (entry.status === 'new') {
                return await this.saveNew(
                    entry,
                    patientRef,
                    sealed,
                    overwrite,
                    effects
                );
            }
            return await this.saveModified(entry, sealed, overwrite, effects);
        });
        return { outcome, effects };
    }

    private async saveNew(
        entry: PendingStructureSetChanges,
        patientRef: PatientPersistenceRef,
        sealed: SealedStructureSet,
        overwrite: boolean,
        effects: SaveEffect[]
    ): Promise<SaveOutcome> {
        const id = entry.structureSetId;
        const identity = sealed.newIdentity;
        if (!identity || !sealed.bytes) {
            // seal() records both for every resolvable new set; missing here
            // is bookkeeping corruption, not a user condition.
            return {
                kind: 'failed',
                entity: 'structureSet',
                id,
                message: 'No sealed content for the new structure set',
            };
        }

        // Retry-safety: a previous attempt may have created the skeleton
        // and failed later. A ref with an ACKNOWLEDGED token survives and is
        // reused; an unacknowledged one (an earlier adoption that conflicted)
        // is re-read through the idempotent create instead, so every retry
        // judges the row's CURRENT state rather than a stale capture. Even a
        // lost create RESPONSE never duplicates: the sealed SOP UID is the
        // server's idempotency key, so the retried create returns the
        // earlier row.
        let ref = this.refs.getRef(id);
        if (!ref || ref.unacknowledgedVersion) {
            const skeleton = await this.gateway.createSkeleton({
                patientId: patientRef.patientId,
                referencedImageSeriesUid: identity.referencedImageSeriesUid,
                displayName: identity.displayName,
                seriesInstanceUid: identity.seriesInstanceUid,
                sopInstanceUid: identity.sopInstanceUid,
                provenanceTaskId: identity.provenanceTaskId,
            });
            // The sealed bytes are final and the server was asked to adopt
            // THEIR identity. A server that answers with different identity
            // (an older connector that ignored the fields) must be refused —
            // proceeding would upload bytes that permanently disagree with
            // their row.
            if (
                skeleton.seriesInstanceUid !== identity.seriesInstanceUid ||
                (skeleton.studyInstanceUid !== null &&
                    skeleton.studyInstanceUid !== identity.studyInstanceUid)
            ) {
                return {
                    kind: 'failed',
                    entity: 'structureSet',
                    id,
                    message:
                        'The patient database did not adopt the structure ' +
                        "set's identity (an older connector version?). " +
                        'Nothing was uploaded.',
                };
            }
            if (!skeleton.wasNew) {
                // The row predates this request (the retry of a create whose
                // response was lost), so its version token was never
                // acknowledged here and the content PUT below would replace
                // whatever the row now holds WITHOUT a 409. Writing is safe
                // only when there is nothing to replace — the row is still
                // the pristine skeleton this session asked for — or when the
                // user chose overwrite in the conflict dialog.
                if (skeleton.approvalStatus === 'Approved') {
                    // Never overwritable, acknowledged or not: revoking a
                    // colleague's approval is not a recoverable mistake.
                    return {
                        kind: 'conflict',
                        entity: 'structureSet',
                        id,
                        reason: 'approvedOnServer',
                    };
                }
                const pristine =
                    skeleton.structureCount === 0 &&
                    (skeleton.approvalStatus === null ||
                        skeleton.approvalStatus === 'Unapproved') &&
                    skeleton.displayName === identity.displayName;
                if (!pristine && !overwrite) {
                    effects.push({
                        kind: 'setRef',
                        id,
                        ref: {
                            datasetId: skeleton.datasetId,
                            version: skeleton.version,
                            unacknowledgedVersion: true,
                        },
                    });
                    return {
                        kind: 'conflict',
                        entity: 'structureSet',
                        id,
                        reason: 'staleVersion',
                        // The row may be this save's own interrupted
                        // create; the conflict dialog says so.
                        ...(skeleton.afterInterruptedAttempt && {
                            afterInterruptedAttempt: true,
                        }),
                    };
                }
            }
            ref = {
                datasetId: skeleton.datasetId,
                version: skeleton.version,
            };
            effects.push({ kind: 'setRef', id, ref });
        } else if (overwrite) {
            // Acknowledged overwrite on a held ref: never re-send the token
            // that just 409'd — fetch the row's live one, with the same
            // approved guard as the modified path.
            const fresh = await this.gateway.getDetail(ref.datasetId);
            if (fresh.approvalStatus === 'Approved') {
                return {
                    kind: 'conflict',
                    entity: 'structureSet',
                    id,
                    reason: 'approvedOnServer',
                };
            }
            ref = { datasetId: ref.datasetId, version: fresh.version };
            effects.push({
                kind: 'updateVersion',
                id,
                version: fresh.version,
            });
        }

        const detail = await this.gateway.replaceContent(
            ref.datasetId,
            ref.version,
            sealed.bytes,
            sealed.rois,
            sealed.lockedRoiIds,
            sealed.modelStructures,
            sealed.roiReviews.reviews
        );
        effects.push({ kind: 'updateVersion', id, version: detail.version });
        // The connector recorded the set's origin when it created the row; the
        // session shows that record from now on, as a reopen would.
        if (detail.origin) {
            effects.push({
                kind: 'adoptStructureSetOrigin',
                id,
                origin: detail.origin,
            });
        }

        let stored = detail;
        // A locally approved/rejected new set: approval is server-
        // authoritative (the content PUT ignores the file's approval tag).
        if (sealed.approvalStatus !== DICOMApprovalStatus.UNAPPROVED) {
            stored = await this.gateway.setApproval(
                ref.datasetId,
                detail.version,
                toWireApproval(sealed.approvalStatus),
                sealed.reviewerName || null
            );
            effects.push({
                kind: 'updateVersion',
                id,
                version: stored.version,
            });
        }

        effects.push({
            kind: 'adoptStructureSetReviews',
            id,
            reviews: stored.roiReviews,
        });
        effects.push({
            kind: 'markStructureSetUploaded',
            id,
            sopInstanceUid: detail.sopInstanceUid,
        });
        return { kind: 'saved', entity: 'structureSet', id };
    }

    private async saveModified(
        entry: PendingStructureSetChanges,
        sealed: SealedStructureSet,
        overwrite: boolean,
        effects: SaveEffect[]
    ): Promise<SaveOutcome> {
        const id = entry.structureSetId;
        const ref = this.refs.getRef(id);
        if (!ref) {
            return {
                kind: 'failed',
                entity: 'structureSet',
                id,
                message: 'Modified structure set has no database identity',
            };
        }

        let version: ConnectorVersionToken = ref.version;
        // Set only when the content is sent, and from the database's answer.
        let storedSopInstanceUid: string | null | undefined;
        if (overwrite) {
            // Acknowledged overwrite: never trust a cached conflict token —
            // fetch the row's live one. An APPROVED server copy is never
            // overwritten (revoking a colleague's approval is not a
            // recoverable mistake) — this must hold for approval-only
            // changes too, not just content edits. The one exception is an
            // entry holding only row edits: colour and the edit lock are what
            // the database itself permits on an approved set, and neither
            // touches the approval.
            const fresh = await this.gateway.getDetail(ref.datasetId);
            if (fresh.approvalStatus === 'Approved' && !entry.rowOnly) {
                return {
                    kind: 'conflict',
                    entity: 'structureSet',
                    id,
                    reason: 'approvedOnServer',
                };
            }
            version = fresh.version;
            effects.push({ kind: 'updateVersion', id, version });
        }

        const localStatus = sealed.approvalStatus;

        // Edits the database row carries on its own, sealed only when no
        // content PUT will carry them. Applied before any approval transition
        // and never instead of one: a lock toggled in the same breath as an
        // approval change still has to reach the row.
        for (const [roiId, fields] of sealed.roiRowEdits) {
            version = await this.gateway.updateStructureRow(
                ref.datasetId,
                version,
                roiId,
                fields
            );
            effects.push({ kind: 'updateVersion', id, version });
        }

        // A set left unapproved with its status unchanged needs no transition:
        // every transition clears the structures' own reviews in the database.
        const transition =
            entry.approvalDirty &&
            (entry.approvalStatusChanged ||
                localStatus !== DICOMApprovalStatus.UNAPPROVED);
        // Every transition but an approval goes first: a server copy still
        // approved would 409 the content PUT, and a transition clears the
        // structures' own reviews, which the steps after it state.
        const unapproveFirst =
            transition && localStatus !== DICOMApprovalStatus.APPROVED;
        // The last detail the database answered with: the reviews it records
        // once every step has run.
        let stored: PersistedStructureSetDetail | undefined;
        if (unapproveFirst) {
            stored = await this.gateway.setApproval(
                ref.datasetId,
                version,
                toWireApproval(localStatus),
                sealed.reviewerName || null
            );
            version = stored.version;
            effects.push({ kind: 'updateVersion', id, version });
        }

        if (entry.contentDirty) {
            if (!sealed.bytes) {
                return {
                    kind: 'failed',
                    entity: 'structureSet',
                    id,
                    message: 'No serialized content for the structure set',
                };
            }
            stored = await this.gateway.replaceContent(
                ref.datasetId,
                version,
                sealed.bytes,
                sealed.rois,
                sealed.lockedRoiIds,
                sealed.modelStructures,
                sealed.roiReviews.reviews
            );
            version = stored.version;
            storedSopInstanceUid = stored.sopInstanceUid;
            effects.push({ kind: 'updateVersion', id, version });
        } else if (sealed.roiReviews.changed) {
            stored = await this.gateway.replaceStructureReviews(
                ref.datasetId,
                version,
                sealed.roiReviews.reviews
            );
            version = stored.version;
            effects.push({ kind: 'updateVersion', id, version });
        }

        if (transition && !unapproveFirst) {
            stored = await this.gateway.setApproval(
                ref.datasetId,
                version,
                toWireApproval(localStatus),
                sealed.reviewerName || null
            );
            effects.push({
                kind: 'updateVersion',
                id,
                version: stored.version,
            });
        }

        if (stored) {
            effects.push({
                kind: 'adoptStructureSetReviews',
                id,
                reviews: stored.roiReviews,
            });
        }
        effects.push({
            kind: 'markStructureSetUploaded',
            id,
            sopInstanceUid: storedSopInstanceUid,
        });
        return { kind: 'saved', entity: 'structureSet', id };
    }
}

/**
 * Captures the structures' own reviews in the seal window, as the statement
 * the save sends: the stored reviews that still judge the content, and the
 * decisions made since load. The connector clears every review the statement
 * does not keep or decide.
 *
 * A stored review is kept only when the database still holds it and it still
 * applies. It does not when the structure's review changed since load, when
 * the structure was added since (the database holds no review for it), or
 * when the review held now
 * is a new decision with the same outcome: an approved structure cannot be
 * changed, so any change the file carries means it was approved again, and a
 * contour change clears a rejection, so a rejection after one was made again,
 * unless an undo restored the recorded one with the geometry it judged.
 * A new set holds no stored review, and neither does a set that was approved
 * or rejected at load, because every set transition clears them.
 *
 * A save that approves the set sends the approval after the content, so until
 * it lands the stored reviews still stand: the statement keeps each one the
 * file leaves unchanged, and a failed approval clears none of them.
 */
function sealRoiReviews(
    entry: PendingStructureSetChanges,
    ss: StructureSet
): SealedRoiReviews {
    const kept: RoiId[] = [];
    const accepted: RoiId[] = [];
    const rejected: RoiId[] = [];
    const sealed = (changed: boolean): SealedRoiReviews => ({
        reviews: { kept, accepted, rejected },
        changed,
    });
    if (ss.approvalStatus !== DICOMApprovalStatus.UNAPPROVED) {
        if (ss.approvalStatus === DICOMApprovalStatus.APPROVED) {
            for (const roi of ss.rois) {
                const change = entry.roiChanges.get(roi.id);
                if (change === undefined || !hasFileBorneChange(change)) {
                    kept.push(roi.id);
                }
            }
        }
        return sealed(false);
    }
    const rowHoldsLoadedReviews =
        entry.status !== 'new' && !entry.approvalStatusChanged;
    let changed = false;
    for (const roi of ss.rois) {
        const change = entry.roiChanges.get(roi.id);
        const reviewChanged =
            (change?.kinds.has('approvalChanged') ?? false) ||
            (change?.kinds.has('added') ?? false);
        // On a row that holds the loaded reviews, a changed review needs the
        // statement even when it clears one.
        changed ||= rowHoldsLoadedReviews && reviewChanged;
        if (roi.approvalStatus === DICOMApprovalStatus.UNAPPROVED) {
            continue;
        }
        const decidedAgain =
            change !== undefined &&
            (roi.approvalStatus === DICOMApprovalStatus.APPROVED
                ? hasFileBorneChange(change)
                : change.kinds.has('contoursEdited') &&
                  roi.recordedReview === null);
        if (rowHoldsLoadedReviews && !reviewChanged && !decidedAgain) {
            kept.push(roi.id);
        } else {
            changed = true;
            (roi.approvalStatus === DICOMApprovalStatus.APPROVED
                ? accepted
                : rejected
            ).push(roi.id);
        }
    }
    return sealed(changed);
}

/**
 * Captures the row fields the per-structure metadata PATCH will transmit: for
 * each changed ROI, the fields that actually changed and nothing else.
 */
function sealRoiRowEdits(
    entry: PendingStructureSetChanges,
    ss: StructureSet
): ReadonlyMap<RoiId, SealedRoiRowEdit> {
    const edits = new Map<RoiId, SealedRoiRowEdit>();
    if (entry.contentDirty) {
        return edits; // The file and its lock declaration carry everything.
    }
    for (const change of entry.roiChanges.values()) {
        const roi = ss.getRoiById(change.roiId);
        if (roi) {
            const fields: {
                -readonly [K in keyof SealedRoiRowEdit]: SealedRoiRowEdit[K];
            } = {};
            if (change.kinds.has('colorChanged')) {
                const { R, G, B } = roi.color;
                fields.color = `${R}\\${G}\\${B}`;
            }
            if (change.kinds.has('lockChanged')) {
                fields.locked = roi.locked;
            }
            // A per-ROI approval flip is not a PATCH: it travels in the review
            // statement, beside the content or alone through the
            // structure-reviews PUT, which records who made it.
            if (fields.color !== undefined || fields.locked !== undefined) {
                edits.set(change.roiId, fields);
            }
        }
    }
    return edits;
}
